/**
 * @file Result 结果页块
 * @description 成功 / 警告 / 错误 / 信息结果态 + 操作区。
 *
 * @whenToUse 提交完成页、404/无权限块、支付结果。
 * @whenNotToUse 列表空态（用 Empty）；页内轻提示（用 Alert / Toast）。
 */

import type { ReactNode } from 'react'
import {
  CircleAlert,
  CircleCheck,
  Info,
  TriangleAlert,
} from 'lucide-react'
import { cn } from '@/lib/cn.ts'

export type ResultStatus = 'success' | 'warning' | 'danger' | 'info'

export type ResultProps = {
  status?: ResultStatus
  title: ReactNode
  description?: ReactNode
  /** 底部操作 */
  extra?: ReactNode
  /** 自定义图标 */
  icon?: ReactNode
  className?: string
}

const iconMap: Record<ResultStatus, ReactNode> = {
  success: <CircleCheck className="size-12 text-success" aria-hidden />,
  warning: <TriangleAlert className="size-12 text-warning" aria-hidden />,
  danger: <CircleAlert className="size-12 text-danger" aria-hidden />,
  info: <Info className="size-12 text-primary" aria-hidden />,
}

export function Result({
  status = 'info',
  title,
  description,
  extra,
  icon,
  className,
}: ResultProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-4 px-6 py-12 text-center',
        className,
      )}
    >
      <div>{icon ?? iconMap[status]}</div>
      <div className="space-y-2">
        <h2 className="text-xl font-semibold text-foreground">{title}</h2>
        {description ? (
          <p className="max-w-md text-sm text-muted">{description}</p>
        ) : null}
      </div>
      {extra ? <div className="flex flex-wrap justify-center gap-2">{extra}</div> : null}
    </div>
  )
}
